<template>
  <div
    :class="[
      'surface-add-post',
      'normalize-typography',
      'right-0',
      isDraftContextMenuShowing ? 'z-backdrop' : 'z-addpost',
    ]"
    @click.stop="recordWhitelistedClick"
  >
    <surface-map-location-picker v-if="canShowMapLocationPicker" v-show="canPost" />
    <div
      v-else-if="canShowButton"
      :class="[
        'add-post-button',
        'relative',
        'mb-5',
        'mr-5',
        isUsingNewComposerModal && 'pb-safe-inset-bottom md:pb-0',
      ]"
    >
      <button
        data-cy="addPostButton"
        data-pw="addPostButton"
        :class="[
          'floating-action-button',
          isUsingNewSurfaceShadows ? 'shadow-elevation-2' : 'shadow-z2 hover-hover:hover:shadow-z3',
        ]"
        :title="__('Add post')"
        @click="addPost"
      >
        <base-icon class="add-icon" name="add" />
        <base-icon class="create-icon" name="create" />
      </button>
      <div
        v-if="showMessage"
        :class="['add-post-message', 'transform', '-translate-x-full', '-left-2', 'after:absolute', 'after:-right-2']"
      >
        {{ __('Double click anywhere, drag files in, paste from clipboard, or click here to post.') }}
      </div>
    </div>
    <div v-if="uploads.length > 0 && !isUsingNewComposerModal" class="add-post-uploads">
      <upload-file
        v-for="upload in uploads"
        :key="fileId(upload)"
        :file="upload"
        :max-file-size="maxFileUploadSize"
        @error="onUploadError"
        @cancel="onUploadCancel"
        @done="onUploadDone"
        @reject="onUploadReject"
      />
    </div>
    <surface-section-picker v-if="showSectionPicker" />
    <surface-content-picker class="add-post-picker" @reject="onUploadReject" />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/camelcase, @typescript-eslint/explicit-function-return-type */
/* N.B.
  Clicks from within this element are not propagated. Look at click.stop attribute
*/
import { mapState, mapGetters, mapActions } from 'vuex'
import { trackEvent } from 'bits/analytics'
import { recordWhitelistedClick } from 'bits/pepin'
import BaseIcon from 'vuecomponents/base_icon'
import SurfaceMapLocationPicker from 'vuecomponents/surface_map_location_picker'
import UploadFile from 'vuecomponents/upload_file'
import SurfaceSectionPicker from 'vuecomponents/surface_section_picker'
import SurfaceContentPicker from 'vuecomponents/content_picker'
import compact from 'lodash.compact'
import { __ } from 'bits/intl'
import uniqueId from 'bits/unique_id'
import appCan from 'bits/app_can'

export default {
  components: {
    BaseIcon,
    SurfaceMapLocationPicker,
    UploadFile,
    SurfaceSectionPicker,
    SurfaceContentPicker,
  },
  computed: {
    ...mapState(['user', 'wall', 'panel', 'nativePostEdit', 'isMobile', 'isReadOnly']),
    ...mapGetters('plans', ['xUpgradeModal']),
    ...mapGetters('post', ['postBeingEditedCid', 'pendingPosts']),
    ...mapGetters([
      'format',
      'canIWrite',
      'isDesktop',
      'isSectioned',
      'isAPanelVisible',
      'isMap',
      'isTable',
      'isUsingNewComposerModal',
      'isUsingNewSurfaceShadows',
      'xNewComposerModalPanel',
      'isApp',
    ]),
    ...mapGetters('table', { tablePostsToUpload: 'postsPendingUpload' }),
    ...mapGetters({ maxFileUploadSize: 'uploadLimit' }),
    ...mapGetters('composerModal', { isComposerModalDocked: 'isDocked' }),
    ...mapGetters('draft', {
      isActiveDraftNewPost: 'isActiveDraftNewPost',
      isDraftContextMenuShowing: 'draftShowingMinimizedContextMenuCid',
    }),
    ...mapGetters('browser', ['isPhone']),
    showMessage() {
      return !this.isMobile
    },
    canShowAddPost() {
      const isReadOnly = this.isReadOnly
      const isAPostBeingEdited = !!this.postBeingEditedCid
      const canIWrite = this.canIWrite
      const isActiveDraftDocked =
        this.xNewComposerModalPanel &&
        this.isComposerModalDocked &&
        this.isDesktop &&
        !this.isPhone &&
        this.isActiveDraftNewPost
      const xUpgradeModal = this.xUpgradeModal

      const isAppWithNewPosting = this.isUsingNewComposerModal && this.isApp && appCan('newPostModal')

      return (
        !isAppWithNewPosting &&
        !isReadOnly &&
        !this.isSectioned &&
        !this.isAPanelVisible &&
        (!isAPostBeingEdited || isActiveDraftDocked) &&
        canIWrite &&
        !xUpgradeModal
      )
    },
    canShowMapLocationPicker() {
      return this.canShowAddPost && this.isMap
    },
    canShowButton() {
      return this.canShowAddPost && !this.isMap && !this.isTable
    },
    canPost() {
      return this.canIWrite && !this.isReadOnly
    },
    uploads() {
      // if there are posts for which sections are yet to be picked,
      // don't start uploading them. filter them out
      if (this.isSectioned) {
        const pendingPostsWithSections = this.pendingPosts.filter((x) => !!x.wall_section_id)
        return compact(pendingPostsWithSections.map((x) => x.file))
      }
      if (this.isTable) return this.tablePostsToUpload.map((post) => post.file)
      return compact(this.pendingPosts.map((x) => x.file))
    },
    showSectionPicker() {
      if (this.isSectioned) {
        const pendingPostsWithoutSections = this.pendingPosts.filter((x) => !x.wall_section_id)
        return pendingPostsWithoutSections.length > 0
      }

      return false
    },
    showPhotoBooth() {
      return true
    },
  },
  methods: {
    ...mapActions(['showOverFileSizeModal']),
    ...mapActions('post', ['startNewPost', 'createPost', 'completePostUpload']),
    ...mapActions('snackbars', ['setSnackbar']),
    addPost() {
      trackEvent('Posts', 'add', 'Plus Button')
      this.startNewPost()
    },
    fileId(file) {
      return uniqueId(file, 'file')
    },
    onUploadError(file, message) {
      this.setSnackbar({ message })
      this.completePostUpload({ file, status: 'error' })
    },
    onUploadDone(file, url) {
      this.completePostUpload({ file, status: url })
    },
    onUploadCancel(file) {
      this.setSnackbar({ message: __('Upload canceled') })
      this.completePostUpload({ file, status: 'cancel' })
    },
    onUploadReject(file) {
      // For now let's just assume that upload was rejected because of size
      this.showOverFileSizeModal({ file })
    },
    onPhotoSelect(file) {
      this.createPost({ attributes: { file: file } })
    },
    onLinkSelect(url) {
      this.createPost({
        attributes: {
          attachment: url,
          wish_content: {
            attachment_props: {
              url: url,
            },
            is_processed: false,
          },
        },
      })
    },
    recordWhitelistedClick,
  },
}
</script>

<style lang="scss">
@import './../../assets/stylesheets/3/modules/all';

.surface-add-post {
  position: fixed;
  // Die IOS die. Piece of shit.
  @supports (-webkit-overflow-scrolling: touch) {
    position: absolute;
  }
  bottom: 0px;
  display: flex;
  align-items: flex-end;
  flex-direction: row-reverse;

  .add-post-message {
    $background-color: $neutral-color;
    display: none;
    background-color: $background-color;
    color: $inverse-text-color;
    padding: 8px 16px;
    border-radius: $default-border-radius;
    box-sizing: border-box;
    position: absolute;
    top: 0px;
    width: 400px;
    @include shadow-z3;

    &:after {
      content: '';
      top: calc(50% - 8px);
      width: 0;
      height: 0;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-left: 8px solid $background-color;
    }
  }

  .floating-action-button {
    width: 56px;
    height: 56px;
    padding: 16px;
    transform-origin: 50% 50%;
    animation-name: expand-icon;
    animation-duration: 0.2s;
    user-select: none;
    border-radius: 50%;
    background-color: $button-color;
    color: white;
    border: none;
    box-sizing: border-box;

    @include hover {
      background-color: darken($button-color, 12%);
      color: white;

      .add-icon {
        display: none;
      }

      .create-icon {
        display: block !important;
        animation-name: rotate-icon;
        animation-duration: 0.2s;
      }

      & + .add-post-message {
        display: block;
      }
    }

    &:active {
      background-color: darken($button-color, 40%);
      color: white;
    }
    &:visited {
      color: white;
    }
  }

  .create-icon {
    display: none !important;
  }

  .add-post-uploads {
    margin-right: 20px;
    display: flex;
    align-items: flex-end;

    .upload-file {
      width: 200px;
      background-color: white;
      border-top-left-radius: $default-border-radius;
      border-top-right-radius: $default-border-radius;
      margin-left: 8px;
    }
  }
}

[data-layout='chat'] .surface-add-post {
  left: 0;
  z-index: 19999;
}

[data-layout='map'] .surface-add-post {
  pointer-events: none; // to be able to click hamburger and profile picture
  top: 0;
  bottom: unset;
  * {
    pointer-events: all; // to be able to click inside the content picker
  }
}

.surface-add-post {
  [data-layout='map'] body.variant-app & {
    top: unset;
    bottom: 0;
  }
  [data-layout='map'] body.variant-phone & {
    top: unset;
    bottom: 0;
  } // tablet should show at top right
}

@include respondToPhone {
  .surface-add-post {
    .add-post-message {
      width: calc(100vw - 100px);
    }
  }
}

@keyframes rotate-icon {
  0% {
    transform: rotate(-90deg);
  }

  100% {
    transform: rotate(0);
  }
}

@keyframes expand-icon {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(1);
  }
}
</style>
